// src/oura/lib/rivalsInstead.ts
//
// Which rivals the client PDF may say "take the answer you should be in" (#4237).
//
// `competitorWins` lists every rival with the questions it is NAMED on
// (`queryCount` / `queries`), and a rival is named on questions the client is
// named on too: the Tongkat Ali scan listed three rivals as "winning" on
// questions where PHYSTA was named by all three engines. Only `insteadCount`
// and `insteadQueries` (ourteam-backend#5124) say a rival was named and the
// client was not, so they alone decide who is listed, and what it is listed for.
//
// ABSENT IS NOT ZERO. A scan from before those fields were sent has no
// `insteadCount` on its rows: nothing can be said either way, so the PDF says
// neither "these rivals beat you" nor "none did".

import type { AiVisibility } from "../audit";

export interface RivalInstead {
    name: string;
    /** Questions it was named on where the business was not. Always above 0. */
    takes: number;
    /** Those questions, as the backend sent them. */
    queries: string[];
}

export interface RivalsInstead {
    /** The rivals that took at least one answer, in the backend's order. */
    rivals: RivalInstead[];
    /**
     * True when the scan measured it and no rival took any: every row carries
     * `insteadCount` and all are 0. Never true for a scan that did not send it.
     */
    none: boolean;
}

export function rivalsInstead(wins: AiVisibility["competitorWins"] | undefined): RivalsInstead {
    const rows = wins ?? [];
    const rivals: RivalInstead[] = [];
    for (const w of rows) {
        if (typeof w.insteadCount === "number" && w.insteadCount > 0) {
            rivals.push({ name: w.name, takes: w.insteadCount, queries: w.insteadQueries ?? [] });
        }
    }
    const measured = rows.length > 0 && rows.every((w) => typeof w.insteadCount === "number");
    return { rivals, none: measured && rivals.length === 0 };
}
